<template>
  <div class="flex items-center justify-between"
       :class="props.class">
    <input
        @focusin="onFocus()"
        type="text"
        inputmode="decimal"
        :value="displayValue"
        @input="onInput"
        @blur="onBlur"
        @keydown="onKeyDown"
        :tabindex="props.tabindex"
        class="h-[32px] w-full rounded-l border text-center px-2 py-1 appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
    />

    <div class="flex flex-col">
      <button
          type="button"
          class="bg-input px-1 hover:bg-gray-300 rounded-tr"
          @click="increment"
          tabindex="-1"
      >
        <Icon icon="mdi:chevron-up"/>
      </button>
      <button
          type="button"
          class="bg-input px-1 hover:bg-gray-300 rounded-br"
          @click="decrement"
          tabindex="-1"
      >
        <Icon icon="mdi:chevron-down"/>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { Icon } from '@iconify/vue'

const props = defineProps({
  modelValue: {
    type: Number,
    required: true,
  },
  min: {
    type: Number,
    default: null,
  },
  max: {
    type: Number,
    default: null,
  },
  step: {
    type: Number,
    default: 1,
  },
  tabindex: {
    type: Number,
    default: 0,
  },
  class: {
    type: String,
    default: '',
  },
  precision: {
    type: Number,
    default: null,
  },
  // Shows "+4.00" / "-2.50" style values while the field isn't focused
  signed: {
    type: Boolean,
    default: false,
  },
  // Clamps typed values to min/max on blur
  clamp: {
    type: Boolean,
    default: false,
  },
  // Value set by the first up/down press while the field is empty (e.g. +0.25 for SPH, 64 for PD)
  startUp: {
    type: Number,
    default: null,
  },
  startDown: {
    type: Number,
    default: null,
  },
})

const emit = defineEmits(['update:modelValue', 'change', 'focus'])

const focused = ref(false)

function display(val) {
  if (val == null || val === '' || isNaN(Number(val))) return ''
  if (!props.signed || focused.value) return String(val)
  const n = Number(val)
  const text = props.precision !== null ? n.toFixed(props.precision) : String(n)
  return n > 0 ? `+${text}` : text
}

const inputValue = ref(props.modelValue)
const displayValue = ref(display(props.modelValue))

watch(() => props.modelValue, (val) => {
  inputValue.value = val
  // Only sync display if user isn't mid-typing a number like "-0" or "-0."
  const currentNum = Number(displayValue.value)
  if (isNaN(currentNum) || currentNum !== val) {
    displayValue.value = display(val)
  }
})

function formatIfNeeded(value) {
  if (props.precision === null) return value

  const [_, decimals] = value.toString().split('.')
  if (decimals && decimals.length > props.precision) {
    return Number(value).toFixed(props.precision)
  }

  return value
}

function isEmptyValue() {
  return inputValue.value == null || inputValue.value === '' || isNaN(Number(inputValue.value))
}

function setValue(value) {
  const formatted = formatIfNeeded(value)
  emit('update:modelValue', Number(formatted))
  emit('change', Number(formatted))
  inputValue.value = formatted
  displayValue.value = display(formatted)
}

function increment() {
  if (props.startUp !== null && isEmptyValue()) {
    setValue(props.startUp)
    return
  }
  let current = Number(inputValue.value)
  if (isNaN(current)) {
    current = props.min ?? 0
  }

  const next = current + props.step
  const formatted = formatIfNeeded(next)

  if (props.max === null || next <= props.max) {
    emit('update:modelValue', Number(formatted))
    emit('change', Number(formatted))
    inputValue.value = formatted
    displayValue.value = display(formatted)
  } else if (props.max !== null) {
    emit('update:modelValue', props.max)
    emit('change', props.max)
    inputValue.value = props.max
    displayValue.value = display(props.max)
  }
}

function decrement() {
  if (props.startDown !== null && isEmptyValue()) {
    setValue(props.startDown)
    return
  }
  let current = Number(inputValue.value)
  if (isNaN(current)) {
    current = props.min ?? 0
  }

  const next = current - props.step
  const formatted = formatIfNeeded(next)

  if (props.min === null || next >= props.min) {
    emit('update:modelValue', Number(formatted))
    emit('change', Number(formatted))
    inputValue.value = formatted
    displayValue.value = display(formatted)
  } else if (props.min !== null) {
    emit('update:modelValue', props.min)
    emit('change', props.min)
    inputValue.value = props.min
    displayValue.value = display(props.min)
  }
}

function onInput(event) {
  const val = event.target.value
  displayValue.value = val

  if (val === '' || val === '-' || (props.signed && val === '+')) {
    inputValue.value = undefined
    emit('update:modelValue', undefined)
    emit('change', undefined)
  } else if ((props.signed ? /^[-+]?(\d+\.?|\d*\.\d*)$/ : /^-?(\d+\.?|\d*\.\d*)$/).test(val)) {
    // Allow intermediate states like "-0", "-0.", "0." without emitting yet
    const numberVal = Number(val)
    if (!isNaN(numberVal) && !val.endsWith('.') && val !== '-0') {
      inputValue.value = numberVal
      emit('update:modelValue', numberVal)
      emit('change', numberVal)
    }
  } else {
    // Invalid input — revert display to last known good value
    event.target.value = displayValue.value = inputValue.value != null ? String(inputValue.value) : ''
  }
}

function onBlur() {
  focused.value = false
  // Flush any intermediate display state ("-0", "0.", etc.) to a real number
  let parsed = Number(displayValue.value)
  if (displayValue.value !== '' && !isNaN(parsed)) {
    if (props.clamp) {
      if (props.min !== null) parsed = Math.max(props.min, parsed)
      if (props.max !== null) parsed = Math.min(props.max, parsed)
    }
    const formatted = props.precision !== null ? formatIfNeeded(parsed) : parsed
    inputValue.value = formatted
    displayValue.value = display(formatted)
    emit('update:modelValue', Number(formatted))
    emit('change', Number(formatted))
  } else if (displayValue.value === '' || displayValue.value === '-' || displayValue.value === '+') {
    displayValue.value = ''
  }
}

function onKeyDown(event) {
  if (event.key === 'ArrowUp') {
    event.preventDefault()
    increment()
  } else if (event.key === 'ArrowDown') {
    event.preventDefault()
    decrement()
  }
}

function onFocus() {
  focused.value = true
  if (props.signed) displayValue.value = display(inputValue.value)
  emit('focus')
}
</script>
